import type { IStore } from "~/state/store";
import type WasmStoreMapping from "../mapping";
import Wasm from "../wasm";
import { TWasmId } from "~/interfaces";
import { updateMarker } from "~/state/map-content/state";
import { openPanoramaAt } from "~/util/providers/pano";
import { getPOI } from "~/route/utils";
import Scene from "~/scene";
import WasmStoreMarker from "~/wasm/store/wasm-store-marker";
import type CursorManager from "~/util/cursorManager";
import { WASM_ZINDEX } from "../const";
import { isRouteActive } from "./util";
import { getPointAndFraction } from "../utils";

let dragDeltaX = 0;
let dragDeltaY = 0;
let routeDNDMarker: WasmStoreMarker = null;
let draggingCursor = null;

export async function onDragStart(wasmId: TWasmId, event: PointerEvent, store: IStore, cursorManager: CursorManager, mapping: WasmStoreMapping, wasm: Wasm) {
	const { wasmMarker: draggedMarker, wasmGeometry: draggedGeometry } = mapping.getWasmToStoreData(wasmId);

	if (event.ctrlKey || event.metaKey) {
		return false;
	}

	let dragMarkerTest = true;

	if (draggedMarker?.customData?.route && !isRouteActive()) {
		dragMarkerTest = false;
	}

	// obecne tahani markeru jen tim, ze ma draggable = true
	if (draggedMarker?.draggable && dragMarkerTest) {
		draggingCursor = cursorManager.add("grabbing");

		// spocitame si rozdil anchoru a kliku
		const anchor = await wasm.coordsToPixel({
			x: draggedMarker.lon,
			y: draggedMarker.lat,
		});

		dragDeltaX = event.clientX - anchor.x;
		dragDeltaY = event.clientY - anchor.y;

		store.dispatch(updateMarker({
			id: draggedMarker.id,
			data: {
				lastChange: draggedMarker.lastChange + 1,
				bringToTheFront: true,
			},
		}));
	} else {
		dragDeltaX = 0;
		dragDeltaY = 0;
	}

	// tazeni z geometrie rucniho mereni
	if (draggedGeometry?.type === "measure-line") {
		const wgs = await wasm.coordsFromScreenPosition(event.clientX, event.clientY);
		const distanceMeter = Mapy.getComponent("distanceMeter").getMain();
		const items = distanceMeter?.getItems() ?? [];
		const index = draggedGeometry.customData?.index ?? 0;
		const item = items.length > index ? items[index] : null;
		const newItem = distanceMeter?.add([wgs.x, wgs.y], draggedGeometry.customData?.index ?? 0);

		if (item) {
			item._newPoint = newItem;
			item._owner.allowHover = false;
			item._wasEnabled = distanceMeter?.interactivePoint.enabled;
			distanceMeter.interactivePoint.hide(true);
		}

		return newItem?._wasmMarker.id ?? 0;
	// vytazeni z geometrie routovace
	} else if (isRouteActive() && !Mapy.CML.state.route.readOnly && draggedGeometry?.type === "custom" && draggedGeometry.customData?.route && draggedGeometry.customData.route.routeIndex === Mapy.CML.state.route.index) {
		routeDNDMarker?.destroy();

		const wgs = await wasm.coordsFromScreenPosition(event.clientX, event.clientY);

		/* eslint-disable-next-line */
		routeDNDMarker = new WasmStoreMarker({
			draggable: true,
			type: "little",
			lon: wgs.x,
			lat: wgs.y,
			zIndex: WASM_ZINDEX.marker.routeDnd,
			interactionType: "click-hover",
			customData: {
				order: draggedGeometry.customData.order,
				route: {
					newPoint: true,
				},
			},
		});

		return routeDNDMarker.id;
	}

	return draggedMarker?.draggable && dragMarkerTest
		? draggedMarker.id
		: 0;
}

export async function onDragMove(id: number, event: PointerEvent, store: IStore, cursorManager: CursorManager, wasm: Wasm) {
	const { markers } = store.getState().mapContent;
	const draggedMarker = markers.find(marker => marker.id === id);

	if (draggedMarker?.draggable) {
		if (!draggingCursor) {
			draggingCursor = cursorManager.add("grabbing");
		}

		// obecna cast pro cokoliv, co ma draggable marker bez ohledu na to, komu ten marker patri
		const wgs = await wasm.coordsFromScreenPosition(event.clientX - dragDeltaX, event.clientY - dragDeltaY);

		store.dispatch(updateMarker({
			id: draggedMarker.id,
			data: {
				lon: wgs.x,
				lat: wgs.y,
				lastChange: draggedMarker.lastChange + 1,
			},
		}));

		// vlastni body - tazeni markeru
		if (Mapy.getComponent("userMarks").isActive() && draggedMarker.type === "user-marks") {
			const items = Mapy.getComponent("userMarks").getMain()?.getItems() || [];

			for (let index = 0; index < items.length; index++) {
				if (index === (draggedMarker.customData?.order ?? 0) - 1) {
					// TODO: az se jednoho dne presune funkcnost z interniho stavu komponenty do store, jeji cast tady se musi smazat, ale pro ted je tohle nejrychlejsi cesta dat z mista, kde se zmena stala, do mista, kde nas zajima
					items[index].dragMove(wgs.x, wgs.y);
					break;
				}
			}
		} else if (Mapy.getComponent("distanceMeter").isActive() && (draggedMarker.type === "measure-points" || draggedMarker.type === "measure-start")) {
			const distanceMeter = Mapy.getComponent("distanceMeter").getMain();
			const index = draggedMarker.customData?.index ?? 0;

			distanceMeter.updateWasmPoint(index, Scene.coordsFromWGS84(wgs.x, wgs.y));
		}
	}
}

export async function onDragEnd(id: number, event: PointerEvent, store: IStore, cursorManager: CursorManager, wasm: Wasm) {
	const { markers } = store.getState().mapContent;
	const draggedMarker = markers.find(marker => marker.id === id);

	if (draggingCursor) {
		cursorManager.remove(draggingCursor);
		draggingCursor = null;
	}

	const wgs = await wasm.coordsFromScreenPosition(event.clientX - dragDeltaX, event.clientY - dragDeltaY);

	// vlastni body - tazeni markeru
	if (Mapy.getComponent("userMarks").isActive() && draggedMarker.type === "user-marks") {
		const items = Mapy.getComponent("userMarks").getMain()?.getItems() || [];

		for (let index = 0; index < items.length; index++) {
			if (index === (draggedMarker.customData?.order ?? 0) - 1) {
				// TODO: az se jednoho dne presune funkcnost z interniho stavu komponenty do store, jeji cast tady se musi smazat, ale pro ted je tohle nejrychlejsi cesta dat z mista, kde se zmena stala, do mista, kde nas zajima
				items[index].dragStop();
				break;
			}
		}
	} else if (Mapy.getComponent("distanceMeter").isActive() && (draggedMarker.type === "measure-points" || draggedMarker.type === "measure-start")) {
		const distanceMeter = Mapy.getComponent("distanceMeter").getMain();
		const index = draggedMarker.customData?.index ?? 0;

		distanceMeter.updateWasmPoint(index, Scene.coordsFromWGS84(wgs.x, wgs.y));
		distanceMeter.allowHover = true;
		distanceMeter.sectionHighlight(true, index, true);
	// marker oko panoramy
	} else if (draggedMarker.type === "pano") {
		const zoom = store.getState().map.zoom;
		const selYear = Mapy.getComponent("pano").getYear();

		openPanoramaAt({
			lon: wgs.x,
			lat: wgs.y,
			zoom,
			selYear,
		}).then(() => {
			Mapy.getComponent("history").save();
		}, () => {});
	// marker routovace
	} else if (isRouteActive() && draggedMarker?.customData?.route) {
		const coords = Scene.coordsFromWGS84(wgs.x, wgs.y);
		const routeState = Mapy.CML.state.route;

		if (draggedMarker.customData.route.isTripWaypoint) {
			// nacitame -> nebudeme menit polohu
			if (routeState.loading) {
				return;
			}

			const curWaypoints = Scene.stringToCoords(routeState.params.waypoints);

			curWaypoints.splice(draggedMarker.customData.order - 1, 1, coords);

			Mapy.CML.state = {
				route: {
					params: {
						waypoints: Scene.coordsToString(curWaypoints),
					},
				},
			};
		} else if (routeState.items.length === 1 && routeState.items[0].tripData) {
			let waypoints = [];

			if (routeState.params.waypoints) {
				waypoints = routeState.params.waypoints;
			} else {
				waypoints = routeState.items[0].tripData[routeState.index].triplist[routeState.params.tripIndex].waypoints;
			}

			const waypointCoords = Scene.stringToCoords(waypoints);
			const lastPointFraction = getPointAndFraction(waypointCoords.map(item => item.toMercator()), coords.toMercator());

			// tahame nas fake bod, ktery vznikl vytazenim z geometrie
			if (draggedMarker.customData.route.newPoint) {
				routeDNDMarker?.destroy();
				routeDNDMarker = null;
			}

			if (lastPointFraction.ind !== -1) {
				waypointCoords.splice(lastPointFraction.ind + 1, 0, coords);

				const route: {
					[key: string]: any;
				} = {
					params: {
						waypoints: Scene.coordsToString(waypointCoords),
						tripIndex: 0,
					},
				};

				if (Mapy.CML.state.route.likeData) {
					if ("owner_name" in Mapy.CML.state.route.likeData) {
						Mapy.CML.state = {
							route: {
								likeData: null,
								likeDataChange: false,
							},
						};

						Mapy.getComponent("dimcurator").setState();
					} else {
						route.likeDataChange = true;
					}
				}
				// nastavime do params
				Mapy.CML.state = {
					route,
				};
			}
		} else {
			// nacitame -> nebudeme menit polohu
			if (routeState.request) {
				return;
			}

			Mapy.CML.state = {
				route: {
					loading: true,
				},
			};

			getPOI(coords).then(data => {
				const items: Array<any> = Mapy.CML.state.route.items;

				if (!items[draggedMarker.customData.order - 1]) {
					return;
				}

				// tahame nas fake bod, ktery vznikl vytazenim z geometrie
				if (draggedMarker.customData.route.newPoint) {
					routeDNDMarker?.destroy();
					routeDNDMarker = null;
					items.splice(draggedMarker.customData.order, 0, {
						usertitle: "",
						favoriteId: "",
						...data,
					});
				// tahame existujici bod routy
				} else {
					Object.assign(items[draggedMarker.customData.order - 1], {
						usertitle: "",
						favoriteId: "",
					}, data);
				}
				Mapy.CML.state = {
					route: {
						items,
						loading: false,
					},
				};
				Mapy.getComponent("history").save();
			}, () => {});
		}
	}
}
